import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth config
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

export default function PaymentCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [creditsAdded, setCreditsAdded] = useState(0);

  useEffect(() => {
    const orderNumber = searchParams.get('order');
    const paymentStatus = searchParams.get('status');
    
    if (orderNumber) {
      // If status is already success from callback, show success immediately
      if (paymentStatus === 'success') {
        verifyPayment(orderNumber);
      } else {
        verifyPayment(orderNumber);
      }
    } else {
      setStatus('failed');
    }
  }, [searchParams]);

  const verifyPayment = async (orderNumber) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/verify/${orderNumber}`,
        getAuthConfig()
      );
      
      if (response.data.status === 'success') {
        setStatus('success');
        setCreditsAdded(response.data.credits_added || response.data.quantity || 0);
        toast.success('Pembayaran berjaya!');
      } else if (response.data.status === 'pending') {
        // Payment still pending, might need to wait
        setStatus('pending');
      } else {
        setStatus('failed');
        toast.error('Pembayaran gagal atau masih dalam proses');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      // If 401, redirect to login
      if (error.response?.status === 401) {
        toast.error('Sila log masuk semula');
        navigate('/login');
        return;
      }
      setStatus('failed');
      toast.error('Gagal mengesahkan pembayaran');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#3D348B] to-[#7678ED] flex items-center justify-center p-4">
      <Helmet>
        <title>Status Pembayaran | Kawkad</title>
      </Helmet>
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-16 h-16 text-[#3D348B] mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Mengesahkan Pembayaran
            </h1>
            <p className="text-gray-600">
              Sila tunggu sebentar...
            </p>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 text-[#F7B801] mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Pembayaran Dalam Proses
            </h1>
            <p className="text-gray-600 mb-6">
              Pembayaran anda sedang diproses. Sila tunggu atau semak semula kemudian.
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full"
              >
                Kembali ke Dashboard
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full rounded-full"
              >
                Semak Semula
              </Button>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Pembayaran Berjaya! 🎉
            </h1>
            <p className="text-gray-600 mb-2">
              Terima kasih atas pembelian anda.
            </p>
            <p className="text-lg font-semibold text-[#3D348B] mb-6">
              {creditsAdded} token kad telah ditambah ke akaun anda.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full py-6 text-lg"
            >
              Kembali ke Dashboard
            </Button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Pembayaran Gagal
            </h1>
            <p className="text-gray-600 mb-6">
              Pembayaran tidak dapat disahkan. Sila cuba lagi atau hubungi sokongan.
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full"
              >
                Kembali ke Dashboard
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full rounded-full"
              >
                Cuba Semak Semula
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
